import {useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {findAllCompanies, deleteCompany} from '../../api/company';
import {deleteProject} from '../../api/project';
import {selectCompanies, addCompany, updateCompanyById, removeCompanyById, setCompanies} from '../../store/companies';
import {addProject, updateProjectById, removeProjectById, removeProjectsByCompanyId} from '../../store/projects';
import {selectProjectsSorted} from '../../store/selectors';
import Modal from '../../components/common/Modal';
import CompanyDetail from './components/CompanyDetail';
import ProjectDetail from './components/ProjectDetail';
import CreateCompanyForm from './components/CreateCompanyForm';
import CreateProjectForm from './components/CreateProjectForm';
import './ProjectsPage.scss';

const ProjectsPage = () => {
    const dispatch = useDispatch();
    const companies = useSelector(selectCompanies);
    const projects = useSelector(selectProjectsSorted);
    const [selected, setSelected] = useState(null); // {type: 'company'|'project', id}
    const [modal, setModal] = useState(null);
    const [mobileView, setMobileView] = useState('list');

    const standaloneProjects = useMemo(() => projects.filter(p => !p.companyId), [projects]);

    const selectedCompany = selected?.type === 'company' ? companies.find(c => c.id === selected.id) : null;
    const selectedProject = selected?.type === 'project' ? projects.find(p => p.id === selected.id) : null;

    const syncCompanies = () =>
        findAllCompanies().then(data => dispatch(setCompanies(data)));

    // ── Company handlers ─────────────────────────────────────────────
    const onCompanyCreated = (company) => {
        dispatch(addCompany(company));
        setModal(null);
        setSelected({type: 'company', id: company.id});
        setMobileView('detail');
    };

    const onCompanySaved = (company) => {
        dispatch(updateCompanyById(company));
        setModal(null);
    };

    const onDeleteCompany = () => {
        if (!selectedCompany || !window.confirm(`Delete company "${selectedCompany.name}"?`)) return;
        deleteCompany(selectedCompany.id).then(() => {
            dispatch(removeCompanyById(selectedCompany.id));
            dispatch(removeProjectsByCompanyId(selectedCompany.id));
            setSelected(null);
            setMobileView('list');
        });
    };

    // ── Project handlers ─────────────────────────────────────────────
    const onProjectCreated = (project) => {
        dispatch(addProject(project));
        setModal(null);
        setSelected({type: 'project', id: project.id});
        setMobileView('detail');
        if (project.companyId) syncCompanies();
    };

    const onProjectSaved = (project) => {
        dispatch(updateProjectById(project));
        setModal(null);
        syncCompanies();
    };

    const onDeleteProject = () => {
        if (!selectedProject || !window.confirm(`Delete project "${selectedProject.title}"?`)) return;
        deleteProject(selectedProject.id).then(() => {
            dispatch(removeProjectById(selectedProject.id));
            setSelected(null);
            setMobileView('list');
        });
    };

    // ── Render ───────────────────────────────────────────────────────
    return (
        <div className="capital-page">
            <div className="capital-page__header">
                <h2 className="capital-page__title">Projects</h2>
                <div className="projects-actions">
                    <button className="wallet-add-button" onClick={() => setModal('company')}>+ Company</button>
                    <button className="wallet-add-button" onClick={() => setModal('project')}>+ Project</button>
                </div>
            </div>

            <div className={`capital-page__body projects-layout${mobileView === 'detail' ? ' is-showing-detail' : ''}`}>
                {/* Left list */}
                <aside className="projects-list">
                    {companies.length === 0 && standaloneProjects.length === 0 && (
                        <p className="projects-empty">No companies or projects yet.</p>
                    )}

                    {companies.map(company => {
                        const companyProjects = projects.filter(p => p.companyId === company.id);
                        const isCompanySelected = selected?.type === 'company' && selected.id === company.id;
                        return (
                            <div key={company.id} className="list-group">
                                <div
                                    className={`list-item list-item--company ${isCompanySelected ? 'is-active' : ''}`}
                                    onClick={() => { setSelected({type: 'company', id: company.id}); setMobileView('detail'); }}
                                >
                                    <span className="list-item__name">{company.name}</span>
                                    <span className="list-item__count">{companyProjects.length}</span>
                                </div>
                                {companyProjects.map(p => (
                                    <div
                                        key={p.id}
                                        className={`list-item list-item--project ${selected?.type === 'project' && selected.id === p.id ? 'is-active' : ''}`}
                                        onClick={() => { setSelected({type: 'project', id: p.id}); setMobileView('detail'); }}
                                    >
                                        <span className="list-item__name">{p.title}</span>
                                        {!p.endDate && <span className="list-item__badge">active</span>}
                                    </div>
                                ))}
                            </div>
                        );
                    })}

                    {standaloneProjects.length > 0 && (
                        <div className="list-group">
                            <div className="list-group__label">Standalone</div>
                            {standaloneProjects.map(p => (
                                <div
                                    key={p.id}
                                    className={`list-item list-item--project ${selected?.type === 'project' && selected.id === p.id ? 'is-active' : ''}`}
                                    onClick={() => { setSelected({type: 'project', id: p.id}); setMobileView('detail'); }}
                                >
                                    <span className="list-item__name">{p.title}</span>
                                    {!p.endDate && <span className="list-item__badge">active</span>}
                                </div>
                            ))}
                        </div>
                    )}
                </aside>

                {/* Right detail */}
                <div className="projects-detail">
                    <button className="mobile-back-btn" onClick={() => setMobileView('list')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M19 12H5M12 5l-7 7 7 7"/>
                        </svg>
                        Projects
                    </button>
                    {selectedCompany && (
                        <CompanyDetail
                            company={selectedCompany}
                            projects={projects}
                            onEdit={() => setModal('edit-company')}
                            onDelete={onDeleteCompany}
                        />
                    )}
                    {selectedProject && (
                        <ProjectDetail
                            project={selectedProject}
                            companies={companies}
                            onEdit={() => setModal('edit-project')}
                            onDelete={onDeleteProject}
                        />
                    )}
                    {!selectedCompany && !selectedProject && (
                        <div className="projects-detail__placeholder">
                            <span>Select a company or project</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal */}
            {modal && (
                <Modal onClose={() => setModal(null)}>
                    {modal === 'company' && (
                        <CreateCompanyForm onCreated={onCompanyCreated} onCancel={() => setModal(null)}/>
                    )}
                    {modal === 'project' && (
                        <CreateProjectForm companies={companies} onCreated={onProjectCreated} onCancel={() => setModal(null)}/>
                    )}
                    {modal === 'edit-company' && (
                        <CreateCompanyForm company={selectedCompany} onCreated={onCompanySaved} onCancel={() => setModal(null)}/>
                    )}
                    {modal === 'edit-project' && (
                        <CreateProjectForm project={selectedProject} companies={companies} onCreated={onProjectSaved} onCancel={() => setModal(null)}/>
                    )}
                </Modal>
            )}
        </div>
    );
};

export default ProjectsPage;
